// P9.1 — True multi-timeframe support (deliverable 4).
//
// COMPLETED-BAR-ONLY semantics: a lower-timeframe bar may reference an HTF bar only after
// that HTF bar has CLOSED (open + duration <= LTF bar close time). There is no partial-HTF
// lookahead, and multi-timeframe behavior is never faked with long lookbacks on one
// timeframe — HTF context comes from a genuine second series, aligned deterministically.
//
// Sessions/DST are computed deterministically from UTC timestamps + an explicit timezone
// spec (std/dst offsets + US/EU/none DST rule) — no Intl, no ambient locale, replayable.
//
// Purity: no I/O, no clock, no randomness.

import type { Bar } from './structuralPrimitives';
import type { TimeframeToken } from './setupIR';

export const TF_MS: Record<TimeframeToken, number> = {
  '30s': 30_000, '1m': 60_000, '5m': 300_000, '15m': 900_000, '30m': 1_800_000,
  '1h': 3_600_000, '4h': 14_400_000, '1d': 86_400_000, '1w': 604_800_000,
};

/** Close time of bar i on timeframe tf (timestamps are BAR-OPEN times). */
export const closeTime = (b: Bar, tf: TimeframeToken): number => b.timestamp + TF_MS[tf];

/** For every LTF bar, the index of the LAST COMPLETED HTF bar visible at that LTF bar's
 *  close (-1 while none). Deterministic two-pointer; O(n+m). This map IS the no-partial-
 *  lookahead contract: htfBars[map[i]] closed at or before ltf close, htfBars[map[i]+1]
 *  had not. */
export function alignCompletedHtf(ltf: readonly Bar[], ltfTf: TimeframeToken, htf: readonly Bar[], htfTf: TimeframeToken): Int32Array {
  const map = new Int32Array(ltf.length).fill(-1);
  let j = -1;
  for (let i = 0; i < ltf.length; i++) {
    const c = closeTime(ltf[i], ltfTf);
    while (j + 1 < htf.length && closeTime(htf[j + 1], htfTf) <= c) j++;
    map[i] = j;
  }
  return map;
}

/** Project an HTF zone/level (knowable at HTF index `htfConfirmedAt`) onto the LTF series:
 *  returns the FIRST LTF index at which the zone is visible (the first LTF bar whose close
 *  is >= the HTF confirming bar's close). Before that index the zone must not be consulted. */
export function projectVisibleFrom(ltf: readonly Bar[], ltfTf: TimeframeToken, htf: readonly Bar[], htfTf: TimeframeToken, htfConfirmedAt: number): number {
  if (htfConfirmedAt < 0 || htfConfirmedAt >= htf.length) return ltf.length; // never visible
  const need = closeTime(htf[htfConfirmedAt], htfTf);
  for (let i = 0; i < ltf.length; i++) if (closeTime(ltf[i], ltfTf) >= need) return i;
  return ltf.length;
}

/** Convert an HTF bar horizon into LTF bars (cross-timeframe expiry). */
export function htfBarsToLtf(htfBars: number, htfTf: TimeframeToken, ltfTf: TimeframeToken): number {
  return Math.round(htfBars * (TF_MS[htfTf] / TF_MS[ltfTf]));
}

/** Deterministic completed-bar OHLCV aggregation on exact timeframe boundaries.
 *  A rolled-up bar is emitted ONLY when its last base bar closes the bucket exactly —
 *  a trailing partial window emits nothing (no partial HTF candle is representable).
 *  (P9.2A orchestration primitive; lives here so both the generation-time provider and
 *  the execution-time bridge share ONE implementation.) */
export function aggregateBars(base: readonly Bar[], baseTf: TimeframeToken, tf: TimeframeToken): Bar[] {
  const w = TF_MS[tf];
  const baseMs = TF_MS[baseTf];
  const out: Bar[] = [];
  let bucketStart = -1, o = 0, h = -Infinity, l = Infinity, c = 0, v = 0, lastCloseInBucket = 0, firstAligned = false;
  const flush = () => {
    // completed = the bucket's FIRST base bar opened exactly at the bucket start AND its
    // last base bar closed exactly at the bucket end (a boundary-partial bucket — e.g. a
    // series starting mid-window — is dropped, never emitted as if complete)
    if (bucketStart >= 0 && firstAligned && lastCloseInBucket === bucketStart + w) {
      out.push({ timestamp: bucketStart, open: o, high: h, low: l, close: c, volume: v });
    }
  };
  for (const b of base) {
    const start = Math.floor(b.timestamp / w) * w;
    if (start !== bucketStart) {
      flush();
      bucketStart = start; o = b.open; h = b.high; l = b.low; c = b.close; v = b.volume;
      firstAligned = b.timestamp === start;
    } else {
      h = Math.max(h, b.high); l = Math.min(l, b.low); c = b.close; v += b.volume;
    }
    lastCloseInBucket = b.timestamp + baseMs;
  }
  flush();
  return out;
}

/** Infer the timeframe token of a bar series from its modal consecutive spacing.
 *  Deterministic; returns null when the spacing matches no known token. */
export function inferTimeframe(bars: readonly Bar[]): TimeframeToken | null {
  if (bars.length < 2) return null;
  const counts = new Map<number, number>();
  for (let i = 1; i < Math.min(bars.length, 500); i++) {
    const d = bars[i].timestamp - bars[i - 1].timestamp;
    counts.set(d, (counts.get(d) ?? 0) + 1);
  }
  let best = 0, bestN = 0;
  for (const [d, n] of counts) if (n > bestN) { best = d; bestN = n; }
  for (const [tok, ms] of Object.entries(TF_MS)) if (ms === best) return tok as TimeframeToken;
  return null;
}

// ── sessions & DST ────────────────────────────────────────────────────────────

export interface TimezoneSpec {
  /** Standard-time offset from UTC in minutes (e.g. New York = -300). */
  stdOffsetMin: number;
  /** DST offset in minutes (e.g. New York = -240). */
  dstOffsetMin: number;
  dstRule: 'US' | 'EU' | 'none';
}
export const TZ_NEW_YORK: TimezoneSpec = { stdOffsetMin: -300, dstOffsetMin: -240, dstRule: 'US' };
export const TZ_LONDON: TimezoneSpec = { stdOffsetMin: 0, dstOffsetMin: 60, dstRule: 'EU' };
export const TZ_UTC: TimezoneSpec = { stdOffsetMin: 0, dstOffsetMin: 0, dstRule: 'none' };

const DAY = 86_400_000;
/** Day-of-week for a UTC timestamp (0=Sunday). 1970-01-01 was a Thursday (=4). */
const dowUtc = (ms: number) => (Math.floor(ms / DAY) + 4) % 7;
/** UTC timestamp of the nth (1-based) given weekday of a month. */
function nthWeekdayUtc(year: number, month0: number, weekday: number, n: number): number {
  const first = Date.UTC(year, month0, 1);
  const shift = (weekday - dowUtc(first) + 7) % 7;
  return first + (shift + (n - 1) * 7) * DAY;
}
function lastWeekdayUtc(year: number, month0: number, weekday: number): number {
  const nextFirst = Date.UTC(year, month0 + 1, 1);
  const lastDay = nextFirst - DAY;
  const back = (dowUtc(lastDay) - weekday + 7) % 7;
  return lastDay - back * DAY;
}

/** Deterministic DST test for a UTC instant under the spec's rule.
 *  US: second Sunday of March 07:00 UTC → first Sunday of November 06:00 UTC (approx of
 *  2:00 local; the one-hour boundary approximation is documented and versioned).
 *  EU: last Sunday of March 01:00 UTC → last Sunday of October 01:00 UTC (exact). */
export function isDst(utcMs: number, tz: TimezoneSpec): boolean {
  if (tz.dstRule === 'none') return false;
  const y = new Date(utcMs).getUTCFullYear();
  if (tz.dstRule === 'US') {
    const start = nthWeekdayUtc(y, 2, 0, 2) + 7 * 3_600_000;
    const end = nthWeekdayUtc(y, 10, 0, 1) + 6 * 3_600_000;
    return utcMs >= start && utcMs < end;
  }
  const start = lastWeekdayUtc(y, 2, 0) + 3_600_000;
  const end = lastWeekdayUtc(y, 9, 0) + 3_600_000;
  return utcMs >= start && utcMs < end;
}

/** Minutes since local midnight for a UTC instant under a timezone spec. */
export function localMinutes(utcMs: number, tz: TimezoneSpec): number {
  const off = (isDst(utcMs, tz) ? tz.dstOffsetMin : tz.stdOffsetMin) * 60_000;
  const local = utcMs + off;
  return Math.floor((((local % DAY) + DAY) % DAY) / 60_000);
}

/** Session-window membership for a BAR (evaluated at bar OPEN time — the decision to treat
 *  a bar as in-session is knowable at its open). Supports overnight windows (start>end). */
export function inSessionWindow(barOpenUtcMs: number, tz: TimezoneSpec, startMin: number, endMin: number): boolean {
  const m = localMinutes(barOpenUtcMs, tz);
  return startMin <= endMin ? m >= startMin && m < endMin : m >= startMin || m < endMin;
}

/** Deterministic reference candle: the FIRST bar whose open falls inside the window on the
 *  bar's local calendar day, scanning from `fromIndex`. Returns its index or -1. */
export function firstSessionBar(bars: readonly Bar[], tz: TimezoneSpec, startMin: number, endMin: number, fromIndex: number): number {
  for (let i = Math.max(0, fromIndex); i < bars.length; i++) {
    if (inSessionWindow(bars[i].timestamp, tz, startMin, endMin)) {
      if (i === 0 || !inSessionWindow(bars[i - 1].timestamp, tz, startMin, endMin)) return i; // first bar of the window
    }
  }
  return -1;
}
